/**
 * PreferenciasNomina.js
 *
 * Las preferencias de la pantalla de PreNómina: qué periodo se propone al entrar, con
 * qué columnas nacen las nóminas nuevas, si las incidencias son sólo de consulta y la
 * leyenda que se imprime arriba de la firma del recibo.
 *
 * Son las únicas llaves de `configuracionNomina` que NO mueven un importe, y por eso
 * viven en su propia pestaña y se guardan por su propio endpoint
 * (empresas/editarPreferenciasNomina). `editarConfiguracionNomina` hace un $set
 * incondicional de sus ~17 campos fiscales: guardar desde aquí por ahí dejaría la
 * configuración de ISR de la empresa en blanco.
 *
 * El mismo componente sirve en la pestaña de Configuración Nómina y dentro del modal que
 * abre el engrane de la bandeja — de ahí `anchoLibre` y `onGuardado`.
 *
 * ── Sobre la forma de la pantalla ──────────────────────────────────────────────
 * Va como RENGLONES DE AJUSTE, no como formulario con columna de etiquetas. El `layout`
 * del Form de la casa fija la etiqueta en 8/24 de ancho, y las etiquetas de aquí
 * ("Cadencias que se proponen", "Entrar directo al periodo propuesto") no caben: antd les
 * pone `white-space: nowrap` y las corta a media palabra. Con el título arriba y la
 * explicación debajo no hay nada que cortar, y la explicación queda junto a su control en
 * vez de flotar como un párrafo gris.
 *
 * Los colores salen de la tarjeta de BandejaPrenomina, que es de donde se abre este panel:
 * son la misma pantalla en dos momentos y tienen que leerse igual.
 */

import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { Select, Spin, message, Checkbox, Tooltip } from "antd";
import { TableOutlined } from "@ant-design/icons";

import {
  Form,
  FormItem,
  SelectItem,
  InputNumberItem,
  ButtonItem,
  SwitchItem,
  ErrorItem,
} from "../../../../Components/Items";
import ModalColumnas from "../../PreNomina/ModalColumnas/ModalColumnas";
import RichTextEditor from "../../../../Components/RichTextEditor";

const { Option } = Select;

// Textos dinámicos de la leyenda del recibo. Es la misma lista que resuelve el backend
// (erp_backend/Utils/leyendaReciboNomina.utils.js): si se agrega una allá, se agrega aquí.
// Misma sintaxis `<x>` que el pagaré de ventas.
const TEXTOS_LEYENDA_RECIBO = [
  { key: "<netoPagar>", desc: "Neto a pagar del recibo" },
  { key: "<netoPagarLetras>", desc: "Neto a pagar con letra" },
  { key: "<totalPercepciones>", desc: "Total de percepciones" },
  { key: "<totalDeducciones>", desc: "Total de deducciones" },
  { key: "<nombreEmpleado>", desc: "Nombre del empleado" },
  { key: "<numeroEmpleado>", desc: "Número de empleado" },
  { key: "<rfcEmpleado>", desc: "RFC del empleado" },
  { key: "<curpEmpleado>", desc: "CURP del empleado" },
  { key: "<nssEmpleado>", desc: "NSS del empleado" },
  { key: "<puestoEmpleado>", desc: "Puesto" },
  { key: "<departamentoEmpleado>", desc: "Departamento" },
  { key: "<tipoPeriodo>", desc: "Tipo de periodo (Semanal…)" },
  { key: "<numeroPeriodo>", desc: "Número del periodo" },
  { key: "<ejercicio>", desc: "Ejercicio" },
  { key: "<fechaInicioPeriodo>", desc: "Inicio del periodo" },
  { key: "<fechaFinPeriodo>", desc: "Fin del periodo" },
  { key: "<fechaPago>", desc: "Fecha de pago" },
  { key: "<fechaLargaPago>", desc: "Fecha de pago con letra" },
  { key: "<nombreEmpresa>", desc: "Razón social" },
  { key: "<rfcEmpresa>", desc: "RFC de la empresa" },
];

// ─── Lenguaje visual, heredado de BandejaPrenomina ───────────────────────────
const TINTA = "#17323a";      // títulos
const TEXTO = "#5b6b70";      // explicaciones — más oscuro que el #888 de la tarjeta,
                              // porque aquí son renglones que sí hay que leer, no apoyos
const TENUE = "#8c8c8c";      // datos de apoyo
const CIAN = "#00BFD6";
const CIAN_OSCURO = "#00879a";
const CIAN_TENUE = "#e6feff";
const CIAN_BORDE = "#c7f0f6";
const HAIRLINE = "#eef3f4";

const SECCION = {
  fontSize: 15, fontWeight: 600, color: TINTA,
  paddingBottom: 8, marginTop: 26, marginBottom: 0,
  borderBottom: `1px solid ${HAIRLINE}`,
};

const FILA = {
  display: "flex", alignItems: "flex-start", gap: 24,
  padding: "15px 0",
  borderBottom: `1px solid ${HAIRLINE}`,
};

const FILA_TITULO = { fontSize: 14, fontWeight: 600, color: TINTA, lineHeight: 1.35 };

// El tope de ancho no es decorativo: a 12px, los 620px del panel dan renglones de ~100
// caracteres, y a esa longitud el ojo pierde el salto de línea. 480px caen cerca de 80,
// que es donde se sigue leyendo de corrido.
const FILA_DESC = { fontSize: 12, color: TEXTO, lineHeight: 1.6, marginTop: 4, maxWidth: 480 };

const CONTROL = { flex: "0 0 auto", paddingTop: 1 };

const META = { fontSize: 12, color: TENUE };

/**
 * Un renglón de ajuste. `control` va a la derecha, alineado con el título; lo que llegue
 * como `children` va completo debajo, para los controles que no caben en una orilla (la
 * lista de cadencias, el selector de columnas).
 */
const Fila = ({ titulo, desc, control, children, ultima }) => (
  <div style={ultima ? { ...FILA, borderBottom: "none" } : FILA}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={FILA_TITULO}>{titulo}</div>
      {desc && <div style={FILA_DESC}>{desc}</div>}
      {children && <div style={{ marginTop: 12 }}>{children}</div>}
    </div>
    {control && <div style={CONTROL}>{control}</div>}
  </div>
);

class PreferenciasNomina extends Component {
  formRef = React.createRef();
  leyendaEditorRef = React.createRef();

  state = {
    loading: false,
    cadencias: [],
    conceptosNomina: [],
    // Ni las cadencias ni las columnas viven en el Form: las dos se editan con controles
    // que no son campos de antd (un renglón que se prende entero, y el modal de columnas
    // de la prenómina). Se mandan aparte en onFinish.
    cadenciasElegidas: [],
    columnasPorDefecto: [],
    modalColumnas: false,
    // Distingue "la empresa no tiene cadencias" de "no se pudieron cargar": sin esto, un
    // error de red se vería igual que una empresa nueva y al guardar borraría la
    // preferencia que no se alcanzó a mostrar.
    cadenciasCargadas: false,
  };

  _montado = false;

  componentDidMount() {
    this._montado = true;
    this.consultarDatos();
  }

  componentWillUnmount() {
    this._montado = false;
  }

  consultarDatos() {
    const { empresaId, headersToken } = this.props;
    this.setState({ loading: true });

    // Las tres son independientes. Los dos catálogos son accesorios: si alguno falla, el
    // formulario se pinta igual y sólo queda sin opciones esa sección.
    Promise.all([
      axios.post("empresas/selectConfiguracionNomina", { empresaId }, { headers: headersToken }),
      axios
        .post("tiposPeriodos/selectCadenciasNomina", { empresaId }, { headers: headersToken })
        .catch(() => null),
      axios
        .post("conceptosNomina/selectAllConceptosNomina", { empresaId }, { headers: headersToken })
        .catch(() => null),
    ])
      .then(([resCfg, resCadencias, resConceptos]) => {
        if (!this._montado) return;
        this.setState({
          loading: false,
          cadencias: resCadencias?.data?.success === 2 ? resCadencias.data.cadencias || [] : [],
          cadenciasCargadas: resCadencias?.data?.success === 2,
          conceptosNomina: resConceptos?.data?.success === 2 ? resConceptos.data.data || [] : [],
        });

        if (resCfg.data.success !== 2) {
          message.error(resCfg.data.message || "No se pudo cargar la configuración de nómina");
          return;
        }
        const c = resCfg.data.configuracionNomina || {};
        this.formRef.current?.setFieldsValue({
          diasGraciaPeriodoAnterior: c.diasGraciaPeriodoAnterior ?? 5,
          entrarDirectoPeriodoSugerido: !!c.entrarDirectoPeriodoSugerido,
          vistaInicialTablaPrenomina: c.vistaInicialTablaPrenomina || "importes",
          incidenciasSoloLectura: c.incidenciasSoloLectura === true,
          leyendaReciboNomina: c.leyendaReciboNomina || "",
        });
        this.setState({
          // Los ObjectId llegan como string o como objeto según el serializador; se
          // normalizan aquí para poder compararlos por identidad al pintar el renglón.
          cadenciasElegidas: (c.cadenciasSugerenciaIds || []).map(String),
          columnasPorDefecto: c.columnasVisiblesPorDefecto || [],
        });
      })
      .catch(() => {
        if (!this._montado) return;
        this.setState({ loading: false });
        message.error("Error al cargar las preferencias de nómina");
      });
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  onFinish(values) {
    const { cadenciasCargadas, cadenciasElegidas, columnasPorDefecto } = this.state;
    this.setState({ loading: true });
    axios
      .post(
        "empresas/editarPreferenciasNomina",
        {
          ...values,
          columnasVisiblesPorDefecto: columnasPorDefecto,
          // Sólo si el catálogo llegó. El backend escribe campo por campo, así que
          // omitirlo deja intacto lo guardado — que es lo correcto cuando el control no se
          // alcanzó a mostrar y el usuario nunca pudo cambiarlo.
          ...(cadenciasCargadas ? { cadenciasSugerenciaIds: cadenciasElegidas } : {}),
          empresaId: this.props.empresaId,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (!this._montado) return;
        this.setState({ loading: false });
        if (res.data.success === 2) {
          message.success("Preferencias guardadas");
          // Con lo guardado, para que PreNómina aplique al momento lo que lee de aquí
          // (las incidencias de sólo consulta) sin volver a pedir sus catálogos.
          this.props.onGuardado?.(values);
        } else {
          message.error(res.data.message || "No se pudieron guardar las preferencias");
        }
      })
      .catch(() => {
        if (!this._montado) return;
        this.setState({ loading: false });
        message.error("Error al comunicarse con el servidor");
      });
  }

  alternarCadencia(id) {
    this.setState((prev) => ({
      cadenciasElegidas: prev.cadenciasElegidas.includes(id)
        ? prev.cadenciasElegidas.filter((x) => x !== id)
        : [...prev.cadenciasElegidas, id],
    }));
  }

  etiquetaCadencia(c) {
    const folio = `${c.serie || ""}${c.numero != null ? c.numero : ""}`;
    return c.nombre || folio || "Sin nombre";
  }

  // ── Cadencias ──────────────────────────────────────────────────────────────
  // El renglón repite la forma de las opciones del selector de periodo —barra de color a
  // la izquierda, fondo teñido, texto en cian cuando está activo—, porque es exactamente
  // esa lista la que se está configurando.
  renderCadencias() {
    const { cadencias, cadenciasElegidas, cadenciasCargadas } = this.state;

    if (!cadencias.length) {
      return (
        <div style={{ ...META, fontStyle: "italic" }}>
          {cadenciasCargadas
            ? "Esta empresa todavía no tiene tipos de periodo dados de alta."
            : "No se pudo cargar el catálogo de cadencias."}
        </div>
      );
    }

    const ninguna = !cadenciasElegidas.length;

    return (
      <>
        {cadencias.map((c) => {
          const id = String(c._id);
          const activa = cadenciasElegidas.includes(id);
          const apagada = c.extraordinaria;
          return (
            <div
              key={id}
              style={{
                // `border` primero: las tres llaves de abajo lo sobrescriben, y al revés
                // el atajo ganaría y se perdería la barra de color.
                border: `1px solid ${activa ? CIAN_BORDE : "#f0f0f0"}`,
                borderLeftWidth: 3,
                borderLeftStyle: "solid",
                borderLeftColor: apagada ? "#e8e8e8" : activa ? CIAN : "#dfe6e8",
                background: apagada ? "#fafafa" : activa ? CIAN_TENUE : "#fff",
                borderRadius: 6,
                marginBottom: 5,
                transition: "background .15s ease, border-color .15s ease",
              }}
            >
              <Checkbox
                checked={activa}
                disabled={apagada}
                onChange={() => this.alternarCadencia(id)}
                style={{
                  display: "flex", alignItems: "center", width: "100%",
                  padding: "7px 10px", margin: 0,
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    color: apagada ? "#b0b0b0" : activa ? CIAN_OSCURO : TINTA,
                    fontWeight: activa ? 600 : 400,
                  }}
                >
                  {this.etiquetaCadencia(c)}
                </span>
                {apagada && (
                  <Tooltip title="Sus periodos duran un día, así que siempre quedarían más cerca de hoy que el periodo real y desplazarían la propuesta.">
                    <span style={{ ...META, marginLeft: 8, borderBottom: "1px dotted #d9d9d9" }}>
                      nunca se propone
                    </span>
                  </Tooltip>
                )}
              </Checkbox>
            </div>
          );
        })}

        {ninguna && (
          <div style={{ ...META, marginTop: 8 }}>
            Sin ninguna marcada se proponen todas, que es como funcionaba antes.
          </div>
        )}
      </>
    );
  }

  // ── Columnas predeterminadas ───────────────────────────────────────────────
  // El resumen nombra los conceptos en vez de contarlos: "3 concepto(s)" obliga a abrir el
  // modal para saber si son los correctos.
  resumenColumnas() {
    const { columnasPorDefecto, conceptosNomina } = this.state;
    if (!columnasPorDefecto.length) return null;

    const porId = new Map(conceptosNomina.map((c) => [String(c._id), c]));
    const nombres = columnasPorDefecto
      .map((col) => porId.get(String(col.conceptoNominaId))?.descripcion)
      .filter(Boolean);

    if (!nombres.length) return `${columnasPorDefecto.length} concepto(s)`;
    const visibles = nombres.slice(0, 4).join(", ");
    return nombres.length > 4 ? `${visibles} y ${nombres.length - 4} más` : visibles;
  }

  render() {
    const { anchoLibre } = this.props;
    const { loading, columnasPorDefecto, conceptosNomina, modalColumnas } = this.state;
    const resumen = this.resumenColumnas();

    return (
      <Spin spinning={loading}>
        {/* Ancho fijo también dentro del modal: los renglones se leen de corrido y a 700px
            la explicación se estira más allá de lo cómodo. En el modal va centrado. */}
        <div style={{ maxWidth: 620, ...(anchoLibre ? { margin: "0 auto" } : {}) }}>
          <Form
            ref={this.formRef}
            name="formPreferenciasNomina"
            layoutForm="vertical"
            bottom={
              <ButtonItem
                type="primary"
                htmlType="submit"
                style={{ display: "block", margin: "0 0 0 auto" }}
                loading={loading}
              >
                Guardar
              </ButtonItem>
            }
            onFinish={this.onFinish.bind(this)}
            onFinishFailed={this.onFinishFailed}
          >
            <div style={{ ...SECCION, marginTop: 4 }}>Periodo que se propone</div>

            <Fila
              titulo="Cadencias que se proponen"
              desc="Al entrar a PreNómina viene propuesto el periodo más cercano a hoy. Marca de cuáles. Las que no marques siguen apareciendo completas en la lista: sólo dejan de proponerse solas."
            >
              {this.renderCadencias()}
            </Fila>

            <Fila
              titulo="Días de gracia"
              desc="Cuando un periodo cierra, se sigue proponiendo ese y no el que acaba de abrir. Con 5, uno que cerró el domingo se propone hasta el viernes."
              control={(
                // Con caja y a lo ancho justo: el InputNumber de la casa va sin borde y en
                // rtl, así que suelto se ve como un número flotando en el aire.
                //
                // Sin la palabra "días" al lado, aunque la pida el instinto: el título del
                // renglón ya la dice. Puesta ahí, además, era lo único del panel que podía
                // desbordarse a la derecha, y el modal la cortaba a media palabra.
                <div style={{ width: 88 }}>
                  <FormItem noStyle name="diasGraciaPeriodoAnterior">
                    <InputNumberItem
                      min={0}
                      max={30}
                      step={1}
                      style={{
                        background: "#fff",
                        border: "1px solid #dfe6e8",
                        borderRadius: 6,
                        boxShadow: "none",
                      }}
                    />
                  </FormItem>
                </div>
              )}
            />

            <Fila
              ultima
              titulo="Entrar directo al periodo propuesto"
              desc="Abre la nómina propuesta sin pasar por el selector. Al usar Volver, el selector se queda."
              control={(
                <FormItem noStyle name="entrarDirectoPeriodoSugerido" valuePropName="checked">
                  <SwitchItem />
                </FormItem>
              )}
            />

            <div style={SECCION}>Tabla de la prenómina</div>

            <Fila
              titulo="Vista inicial"
              desc="Con qué pestaña abre la tabla de cada nómina."
              control={(
                <FormItem noStyle name="vistaInicialTablaPrenomina">
                  <SelectItem style={{ width: 190 }}>
                    <Option value="importes">Importes</Option>
                    <Option value="incidencias">Días e Incidencias</Option>
                  </SelectItem>
                </FormItem>
              )}
            />

            <Fila
              ultima
              titulo="Columnas predeterminadas"
              desc="Los conceptos que elijas nacen como columna propia en cada nómina nueva; los demás se suman en Otras Percepciones y Otras Deducciones. Las nóminas que ya existen conservan sus columnas."
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <ButtonItem
                  blanco
                  lessTime
                  icon={<TableOutlined />}
                  disabled={!conceptosNomina.length}
                  onClick={() => this.setState({ modalColumnas: true })}
                >
                  {columnasPorDefecto.length ? "Cambiar columnas" : "Elegir columnas"}
                </ButtonItem>

                {resumen ? (
                  <span style={{ fontSize: 12, color: CIAN_OSCURO, flex: 1, minWidth: 0 }}>
                    {resumen}
                  </span>
                ) : (
                  <span style={{ ...META, flex: 1, minWidth: 0 }}>
                    {conceptosNomina.length
                      ? "Ninguno — todo cae en Otras Percepciones y Otras Deducciones."
                      : "No se pudo cargar el catálogo de conceptos."}
                  </span>
                )}
              </div>
            </Fila>

            <div style={SECCION}>Incidencias</div>

            <Fila
              ultima
              titulo="Incidencias sólo para consulta"
              desc="Las incidencias de la prenómina se ven pero no se modifican: no se capturan, no se limpian ni se importan del reloj checador o de Excel. Las vacaciones, incapacidades e incidencias permanentes siguen entrando al calcular."
              control={(
                <FormItem noStyle name="incidenciasSoloLectura" valuePropName="checked">
                  <SwitchItem />
                </FormItem>
              )}
            />

            <div style={SECCION}>Recibo de nómina</div>

            <Fila
              ultima
              titulo="Leyenda arriba de la firma"
              desc="Se imprime en cada recibo, justo arriba de la firma del empleado. Da clic en un texto dinámico para insertarlo donde está el cursor; en el recibo se cambia por el dato de ese empleado. Vacío, el recibo no lleva leyenda."
            >
              <FormItem noStyle name="leyendaReciboNomina">
                <RichTextEditor
                  ref={this.leyendaEditorRef}
                  placeholder="Ej.: Acepto que recibí la cantidad de <netoPagar> (<netoPagarLetras>) por concepto de mi nómina <tipoPeriodo> número <numeroPeriodo>, sin que se me adeude cantidad alguna."
                  height={120}
                  plainText
                />
              </FormItem>

              <div style={{ ...META, marginTop: 12, marginBottom: 6 }}>Textos dinámicos</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 6 }}>
                {TEXTOS_LEYENDA_RECIBO.map(({ key, desc }) => (
                  <div
                    key={key}
                    onClick={() => this.leyendaEditorRef.current?.insertText(key)}
                    style={{
                      background: "#fff",
                      border: `1px solid ${CIAN_BORDE}`,
                      borderRadius: 4,
                      padding: "4px 6px",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontFamily: "monospace", fontSize: 10, color: CIAN_OSCURO, fontWeight: 600, lineHeight: 1.3 }}>
                      {key}
                    </div>
                    <div style={{ fontSize: 10, color: TENUE, lineHeight: 1.2 }}>{desc}</div>
                  </div>
                ))}
              </div>
            </Fila>
          </Form>
        </div>

        {/* Montaje condicional obligatorio: ModalColumnas arma su estado interno una sola
            vez, en el constructor. Si se dejara montado, al reabrirlo mostraría los
            checkboxes de la primera apertura y no lo último elegido. */}
        {modalColumnas && (
          <ModalColumnas
            visible
            titulo="Columnas predeterminadas de las nóminas nuevas"
            ayuda="Se aplican al crear una prenómina; las que ya existen no cambian."
            textoBoton="Usar estas columnas"
            conceptosNomina={conceptosNomina}
            columnasActuales={columnasPorDefecto}
            onClose={() => this.setState({ modalColumnas: false })}
            // No llama al servidor: la selección se persiste con el botón Guardar del
            // formulario, junto con el resto de las preferencias.
            onGuardar={(cols) => this.setState({ columnasPorDefecto: cols, modalColumnas: false })}
          />
        )}
      </Spin>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(PreferenciasNomina);
